import { RecentlyViewedChangeEvent } from "@theme/events";

/** Stores a short product history only while Shopify permits personalization. */
export class RecentlyViewed {
  static #STORAGE_KEY = "viewedProducts";
  static #MAX_PRODUCTS = 4;
  static #CONSENT_KEY = "viewedProductsConsent";
  static #latestConsent;
  static #receivedAcceptance = false;
  static #consentStorageUnavailable = false;
  static #initialized = false;
  static #loadAttempted = false;
  static #permission;
  static #lastShopifyPermission;
  static #revision = 0;
  static #currentProduct;
  static #recorded = false;
  static #recording = false;
  static #pendingDeletion = false;
  static #remoteDenied = false;
  static #channel;

  /**
   * Initialize once per document, optionally registering its eligible product.
   * @param {string} [productId]
   */
  static initialize(productId) {
    if (productId && productId !== this.#currentProduct) {
      this.#currentProduct = productId;
      this.#recorded = false;
    }
    if (!this.#initialized) {
      this.#initialized = true;
      // Existing records establish ordering, never permission for a new document.
      this.#syncConsent(true);
      document.addEventListener("visitorConsentCollected", () => {
        this.#syncConsent();
        const permission = this.#shopifyPermission();
        if (typeof permission === "boolean") {
          this.#lastShopifyPermission = permission;
          this.#publishConsent(permission);
        }
        this.#refresh();
      });
      const restore = () => {
        this.#refresh();
        // History may have changed while this document was frozen or unfocused.
        this.#notify();
      };
      window.addEventListener("pageshow", restore);
      window.addEventListener("focus", restore);
      document.addEventListener("visibilitychange", () => {
        if (!document.hidden) restore();
      });
      window.addEventListener("storage", (event) => {
        if (
          event.key !== this.#STORAGE_KEY &&
          event.key !== this.#CONSENT_KEY &&
          event.key !== null
        )
          return;
        if (event.key === this.#CONSENT_KEY && event.newValue) {
          this.#receiveConsent(this.#parseConsent(event.newValue));
        }
        this.#refresh();
        if (event.key !== this.#CONSENT_KEY) this.#notify();
      });
      try {
        if (typeof BroadcastChannel === "function") {
          this.#channel = new BroadcastChannel("fts-recently-viewed-consent");
          this.#channel.addEventListener("message", ({ data }) => {
            this.#receiveConsent(data);
            this.#refresh();
          });
        }
      } catch {
        // Focus, pageshow and storage events still recheck the Shopify API.
      }
      document.addEventListener("DOMContentLoaded", () => this.#refresh(), {
        once: true,
      });
      window.addEventListener("load", () => this.#refresh(), { once: true });
    }
    this.#refresh();
  }

  static #shopifyPermission() {
    try {
      return window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.();
    } catch {
      return undefined;
    }
  }

  static #publishConsent(allowed) {
    const record = {
      version: 1,
      sequence: (this.#latestConsent?.sequence ?? 0) + 1,
      id: crypto.randomUUID(),
      allowed,
    };
    this.#receiveConsent(record, false, true);
    // Each transport is independent. Failed choices are never replayed.
    try {
      localStorage.setItem(this.#CONSENT_KEY, JSON.stringify(record));
    } catch {
      // Broadcasting can still protect other open documents.
    }
    try {
      this.#channel?.postMessage(record);
    } catch {
      // Storage notifications remain available when messaging fails.
    }
  }

  static #parseConsent(value) {
    try {
      return JSON.parse(value);
    } catch {
      return undefined;
    }
  }

  static #receiveConsent(record, baseline = false, local = false) {
    if (
      record?.version !== 1 ||
      !Number.isSafeInteger(record.sequence) ||
      record.sequence < 1 ||
      typeof record.id !== "string" ||
      !record.id ||
      typeof record.allowed !== "boolean"
    )
      return;
    const previous = this.#latestConsent;
    if (
      previous &&
      (record.sequence < previous.sequence ||
        (record.sequence === previous.sequence &&
          (record.id === previous.id || !previous.allowed || record.allowed)))
    )
      return;
    this.#latestConsent = record;
    if (baseline) return;
    this.#receivedAcceptance = record.allowed;
    // Local permission remains governed by Shopify; only remote withdrawal
    // needs to protect against this document retaining a stale allowed API state.
    this.#remoteDenied = !record.allowed && !local;
    // A pre-withdrawal observation cannot establish a later acceptance. Reset
    // before notifying search, whose synchronous listeners recheck permission.
    if (this.#remoteDenied) this.#lastShopifyPermission = undefined;
    if (!record.allowed) {
      this.#recorded = false;
      this.#permission = false;
      this.#remove();
    }
    // Invalidate even when effective permission has not changed.
    this.#notify();
  }

  static #syncConsent(baseline = false) {
    try {
      const record = this.#parseConsent(
        localStorage.getItem(this.#CONSENT_KEY),
      );
      this.#consentStorageUnavailable = false;
      this.#receiveConsent(record, baseline);
    } catch {
      this.#consentStorageUnavailable = true;
    }
  }

  static #readPermission() {
    this.#syncConsent();
    const permission = this.#shopifyPermission();
    const accepted =
      permission === true && this.#lastShopifyPermission === false;
    // Commit the raw observation before synchronous history listeners recheck it.
    // Remote denial must never manufacture a false-to-true Shopify transition.
    if (typeof permission === "boolean")
      this.#lastShopifyPermission = permission;
    if (accepted) this.#publishConsent(true);
    // Publish only a newly observed local withdrawal, not initial/stale rejection
    // or a denial already applied by the shared-record handler above.
    if (
      permission === false &&
      this.#permission === true &&
      !this.#remoteDenied
    ) {
      this.#publishConsent(false);
    }
    return this.#remoteDenied ? false : permission;
  }

  static #refresh() {
    const permission = this.#readPermission();
    const initialReconciliation =
      this.#permission === undefined && this.#receivedAcceptance;
    const changed = permission !== this.#permission;
    this.#permission = permission;
    if (changed) this.#recorded = false;
    // An unchanged rejection may be stale in another tab. Storage/focus events
    // must not erase history saved by a tab that has just received acceptance.
    if (
      (permission === false && changed && !initialReconciliation) ||
      this.#pendingDeletion
    )
      this.#remove();
    if (
      permission === true &&
      !this.#pendingDeletion &&
      !this.#consentStorageUnavailable &&
      this.#currentProduct &&
      !this.#recorded &&
      !this.#recording
    ) {
      this.#recording = true;
      try {
        this.#recorded = this.addProduct(this.#currentProduct) === true;
      } finally {
        this.#recording = false;
      }
    }
    if (changed) this.#notify();
    if (
      this.#shopifyPermission() === undefined &&
      !this.#loadAttempted &&
      typeof window.Shopify?.loadFeatures === "function"
    ) {
      this.#loadAttempted = true;
      try {
        window.Shopify.loadFeatures(
          [{ name: "consent-tracking-api", version: "0.1" }],
          (error) => {
            if (!error) this.#refresh();
          },
        );
      } catch {
        // Unavailable consent must never enable history.
      }
    }
  }

  static get allowed() {
    this.initialize();
    return (
      this.#permission === true &&
      !this.#pendingDeletion &&
      !this.#consentStorageUnavailable
    );
  }

  static get revision() {
    return this.#revision;
  }

  static #notify() {
    this.#revision++;
    document.dispatchEvent(
      new RecentlyViewedChangeEvent(this.#permission === true, this.#revision),
    );
  }

  /** @param {string} productId */
  static addProduct(productId) {
    if (!this.allowed) return;
    const previous = this.getProducts();
    const products = [
      productId,
      ...previous.filter((id) => id !== productId),
    ].slice(0, this.#MAX_PRODUCTS);
    if (JSON.stringify(products) === JSON.stringify(previous)) return true;
    try {
      if (
        this.#pendingDeletion ||
        this.#readPermission() !== true ||
        this.#consentStorageUnavailable
      )
        return;
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(products));
      this.#notify();
      return true;
    } catch {
      // Storage can be disabled independently of consent.
    }
  }

  static #remove() {
    this.#pendingDeletion = true;
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
      this.#pendingDeletion = false;
    } catch {
      // Retry before any future read or write; rejected history must not return.
    }
  }

  static clearProducts() {
    this.#recorded = true;
    this.#remove();
    this.#notify();
  }

  /** @returns {string[]} */
  static getProducts() {
    if (!this.allowed) return [];
    try {
      const products = JSON.parse(
        localStorage.getItem(this.#STORAGE_KEY) || "[]",
      );
      if (
        !Array.isArray(products) ||
        !products.every((id) => typeof id === "string" && /^\d+$/.test(id))
      ) {
        this.#remove();
        return [];
      }
      return [...new Set(products)].slice(0, this.#MAX_PRODUCTS);
    } catch {
      this.#remove();
      return [];
    }
  }
}
